import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { RefreshCw, BarChart3, PieChart as PieChartIcon } from 'lucide-react';
import { useAppSelector } from '../../../store/hooks';
import type { RootState } from '../../../store';
import { marketingService } from '../../../services/api';
import MaskedMobile from '../../common/MaskedMobile';
import TableSkeleton from '../../common/TableSkeleton';
import LeadConversionCard from './LeadConversionCard';
import type { LeadConversion } from '../../../types';
import Select from '../../common/Select';
import { personOption, staffFilterValue } from '../../common/StaffFilterOption';

interface Row {
  mobile: string; user_id: string; name: string; role: string;
  /** false once deactivated; missing = active. */
  is_active?: boolean;
  parent_mobile: string; parent_name: string;
  targets: number; target_units: number;
  units_direct: number; units_indirect: number; units_total: number;
  // Orders already PAID but not yet incentive-approved — units_direct/
  // units_indirect only count approved orders (matching /targets), so a
  // paid-but-pending order shows up here instead of being invisible.
  units_direct_pending?: number;
  units_indirect_pending?: number;
  directly_earned_amount: number; indirect_earned_amount: number; total_earned_amount: number;
  // Sale revenue (not incentive payout) for the same pending orders above.
  direct_pending_sales_amount?: number;
  indirect_pending_sales_amount?: number;
  achievement_pct: number | null; remaining_units: number;
  leads: Record<string, number>;
  leads_uploaded: number;
  leads_uploaded_used: number;
}

interface Organisation {
  uploaded: number; used: number; unused: number;
  assigned: number; onboarded: number;
  usage_pct: number | null;
  // onboarded/uploaded. Named for what it counts, because the lead-conversion
  // funnel's `conversion_pct` is paid/uploaded — a different question, since a
  // lead can be onboarded without ever paying.
  onboarded_pct: number | null;
}

interface TrendPoint { month: string; uploaded: number; used: number; unused: number }

interface Group {
  mobile: string; name: string; role: string; members: Row[]; member_count: number;
  units_total: number; units_direct: number; units_indirect: number;
  total_earned_amount: number; target_units: number; leads_total: number;
  leads_uploaded: number; leads_uploaded_used: number;
}

interface Totals {
  people: number; target_units: number;
  units_direct: number; units_indirect: number; units_total: number;
  units_direct_pending?: number;
  units_indirect_pending?: number;
  directly_earned_amount: number; indirect_earned_amount: number; total_earned_amount: number;
  direct_pending_sales_amount?: number;
  indirect_pending_sales_amount?: number;
  // Farm staff incentives for the same window. Org-wide rather than
  // team-scoped: farm executives sit outside the marketing reporting tree
  // entirely, so there is no subtree to narrow them by.
  farm_incentives?: number;
  pending_farm_incentives?: number;
  farm_sales?: number;
  farm_executives?: number;
  farm_visits?: number;
  leads: Record<string, number>;
  leads_uploaded: number;
  leads_uploaded_used: number;
}

interface FarmRow {
  mobile: string;
  name: string;
  role: string;
  visits: number;
  sales: number;
  earned: number;
  pending_incentive_amount: number;
}

const inr = (n?: number) => `₹${(n || 0).toLocaleString('en-IN')}`;

const LEAD_SERIES_LABELS: Record<string, string> = {
  Uploaded: 'Uploaded',
  Used: 'Status updated',
  Unused: 'Not updated',
};

// Fixed hue order, not a generated rainbow — TailAdmin indigo leads (slot 1),
// then a validated 7-hue sequence (CVD-safe adjacent pairs), matching the
// same order used across the marketing section's other pie charts.
const LEAD_PIE_COLORS = [
  '#465FFF', '#eb6834', '#1baf7a', '#eda100',
  '#e87ba4', '#008300', '#4a3aa7', '#e34948',
];

const CustomLeadTooltip = ({ active, payload, label }: { active?: boolean; payload?: any[]; label?: string }) => {
  if (active && payload && payload.length) {
    const headerLabel = label ?? payload[0]?.payload?.name ?? payload[0]?.name;
    return (
      <div className="bg-white p-3 rounded-xl shadow-lg border border-slate-200">
        <p className="font-bold text-xs text-slate-500 uppercase tracking-wider mb-2 border-b border-slate-100 pb-1.5">{headerLabel}</p>
        <div className="space-y-1.5">
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center justify-between gap-4">
              <span className="text-[11px] font-medium text-slate-500">{LEAD_SERIES_LABELS[entry.dataKey as string] || entry.name}:</span>
              <span className="text-[12px] font-bold" style={{ color: entry.color }}>{entry.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

const GenericSplitTooltip = ({ active, payload }: { active?: boolean; payload?: any[] }) => {
  if (active && payload && payload.length) {
    const p = payload[0];
    return (
      <div className="bg-white p-3 rounded-xl shadow-lg border border-slate-200">
        <p className="font-bold text-xs text-slate-500 uppercase tracking-wider mb-1">{p.payload?.name ?? p.name}</p>
        <p className="text-sm font-bold" style={{ color: p.payload?.fill ?? p.color }}>{p.payload?.displayValue}</p>
      </div>
    );
  }
  return null;
};

const MiniMultiSeriesTooltip = ({ active, payload }: { active?: boolean; payload?: any[] }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 rounded-xl shadow-lg border border-slate-200">
        <div className="space-y-1.5">
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center justify-between gap-4">
              <span className="text-[11px] font-medium text-slate-500">{entry.dataKey}:</span>
              <span className="text-[12px] font-bold" style={{ color: entry.color }}>
                {entry.payload?.[`${entry.dataKey}Display`] ?? entry.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

// Each chart card owns its own Bar/Pie choice instead of one switcher
// controlling every graph on the page — so comparing "this one as a pie" no
// longer forces every other card into a pie too.
const ChartTypeToggle: React.FC<{ value: 'bar' | 'pie'; onChange: (v: 'bar' | 'pie') => void }> = ({ value, onChange }) => (
  <div className="flex items-center bg-slate-100 rounded-lg p-1 gap-1">
    <button
      type="button"
      onClick={() => onChange('bar')}
      title="Bar chart"
      className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold transition-colors cursor-pointer outline-none focus:outline-none ${value === 'bar' ? 'bg-white shadow-xs text-emerald-700' : 'text-slate-500 hover:text-slate-700'}`}
    >
      <BarChart3 size={13} />
    </button>
    <button
      type="button"
      onClick={() => onChange('pie')}
      title="Pie chart"
      className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold transition-colors cursor-pointer outline-none focus:outline-none ${value === 'pie' ? 'bg-white shadow-xs text-emerald-700' : 'text-slate-500 hover:text-slate-700'}`}
    >
      <PieChartIcon size={13} />
    </button>
  </div>
);

const monthLabel = (ym: string): string => {
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
};

const monthOptions = () => {
  const out: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = 0; i < 12; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({
      value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }),
    });
  }
  return out;
};

/**
 * Per-person marketing overview: targets, units and incentive money split
 * DIRECT/INDIRECT, and the lead pipeline by status.
 *
 * The rows a caller gets are decided by the API from their position in the
 * hierarchy, not by anything this component sends — an Executive sees only
 * themselves, a Lead their Executives, a Manager their subtree (groupable by
 * Marketing Lead), an Admin everyone. The person picker can only narrow that.
 */
const MarketingSummaryTab: React.FC = () => {
  const { adminMobile } = useAppSelector((s: RootState) => s.auth);
  const [rows, setRows] = useState<Row[]>([]);
  // The person picker's full roster — captured whenever a fetch comes back
  // unfiltered (person === ''), so selecting one person doesn't shrink `rows`
  // (used for the table) into the picker's own option list.
  const [allPeople, setAllPeople] = useState<Row[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [totals, setTotals] = useState<Totals | null>(null);
  const [farmRows, setFarmRows] = useState<FarmRow[]>([]);
  const [org, setOrg] = useState<Organisation | null>(null);
  const [trend, setTrend] = useState<TrendPoint[]>([]);
  const [conversion, setConversion] = useState<LeadConversion | null>(null);
  // One toggle for all three columns of this card — they're one visual unit,
  // not three independent cards, so they switch together.
  const [splitChartType, setSplitChartType] = useState<'bar' | 'pie'>('bar');
  const [trendChartType, setTrendChartType] = useState<'bar' | 'pie'>('bar');
  const [period, setPeriod] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const months = useMemo(monthOptions, []);
  const [month, setMonth] = useState(months[0].value);
  const [person, setPerson] = useState('');
  // Table can render grouped-by-lead (see `groups`/`canGroupByLead` below),
  // but no toggle exists yet to switch into that view — stays 'person'.
  const [view] = useState<'person' | 'lead'>('person');

  const load = useCallback(async () => {
    if (!adminMobile) return;
    setLoading(true);
    setError(null);
    try {
      const res = await marketingService.getMarketingSummary(adminMobile, {
        month,
        person: person || undefined,
      });
      if (res?.statuscode === 200) {
        setRows(res.rows || []);
        if (!person) {
          setAllPeople(res.rows || []);
        }
        setGroups(res.groups || []);
        setTotals(res.totals || null);
        setFarmRows(Array.isArray(res.farm_rows) ? res.farm_rows : []);
        setOrg(res.organisation || null);
        setTrend(res.monthly_trend || []);
        setConversion(res.lead_conversion || null);
        setPeriod(res.period || '');
      } else {
        setError(res?.detail || res?.message || 'Failed to load summary');
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load summary');
    } finally {
      setLoading(false);
    }
  }, [adminMobile, month, person]);

  useEffect(() => { load(); }, [load]);

  // Lead-wise only makes sense when a real Marketing Lead heads a team. The
  // '' group is the "no Lead above them" bucket, so on its own it would offer
  // a grouped view that groups nothing.
  const canGroupByLead = useMemo(
    () => groups.some((g) => g.mobile !== ''), [groups]);

  const trendChartData = useMemo(
    () => trend.map((m) => ({
      name: monthLabel(m.month),
      Uploaded: m.uploaded,
      Used: m.used,
      Unused: m.unused,
    })),
    [trend],
  );

  const unitsSplitData = useMemo(() => ([
    { name: 'Direct', value: totals?.units_direct || 0, displayValue: `${totals?.units_direct || 0} units` },
    { name: 'Indirect', value: totals?.units_indirect || 0, displayValue: `${totals?.units_indirect || 0} units` },
  ]), [totals]);

  const incentivesSplitData = useMemo(() => ([
    { name: 'Direct', value: totals?.directly_earned_amount || 0, displayValue: inr(totals?.directly_earned_amount) },
    { name: 'Indirect', value: totals?.indirect_earned_amount || 0, displayValue: inr(totals?.indirect_earned_amount) },
  ]), [totals]);

  const targetTotalBarLineData = useMemo(() => ([{
    name: 'This Period',
    Target: totals?.target_units || 0,
    Total: totals?.units_total || 0,
    TargetDisplay: `${totals?.target_units || 0} units`,
    TotalDisplay: `${totals?.units_total || 0} units`,
  }]), [totals]);

  const targetTotalPieData = useMemo(() => ([
    { name: 'Target', value: totals?.target_units || 0, displayValue: `${totals?.target_units || 0} units` },
    { name: 'Total', value: totals?.units_total || 0, displayValue: `${totals?.units_total || 0} units` },
  ]), [totals]);

  const unitsSplitBarData = useMemo(() => ([{
    name: 'Units',
    Direct: totals?.units_direct || 0,
    Indirect: totals?.units_indirect || 0,
    DirectDisplay: `${totals?.units_direct || 0} units`,
    IndirectDisplay: `${totals?.units_indirect || 0} units`,
  }]), [totals]);

  const incentivesSplitBarData = useMemo(() => ([{
    name: 'Incentives',
    Direct: totals?.directly_earned_amount || 0,
    Indirect: totals?.indirect_earned_amount || 0,
    DirectDisplay: inr(totals?.directly_earned_amount),
    IndirectDisplay: inr(totals?.indirect_earned_amount),
  }]), [totals]);

  const inputCls = 'h-[42px] pl-3 pr-3 rounded-xl border border-slate-200 shadow-sm text-sm font-semibold text-slate-700 bg-white focus:border-[#1B4332] transition-all duration-200';

  const tile = (label: string, value: React.ReactNode, tone = 'text-slate-800',
                sub?: React.ReactNode) => (
    <div>
      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap">{label}</p>
      <p className={`text-xl font-black ${tone}`}>{value}</p>
      {sub ? (
        <p className="text-[10px] font-semibold text-slate-400 leading-tight mt-0.5 whitespace-nowrap">{sub}</p>
      ) : null}
    </div>
  );

  // The backend sends null, not 0, when nothing was uploaded — there is no
  // percentage of zero. Rendering the tile without a sub-label is the honest
  // result; "0%" would claim a measurement nobody made.
  //
  // The denominator is spelled out on screen on purpose. These two ratios share
  // a numerator style but not a meaning (worked vs onboarded), and the API had
  // both under one name until recently; saying "of uploaded" is what stops a
  // reader inferring the wrong base.
  // Projected incentive, shown beneath the earned figure it sits next to. Null
  // when nothing is pending, so a settled month shows no clutter.
  const pendingSub = (value?: number): React.ReactNode => (
    !value ? undefined : (
      <>
        <span className="block text-amber-600">{inr(value)}</span>
        <span className="block font-normal">pending</span>
      </>
    )
  );

  // Orders already paid but not yet incentive-approved — a unit count, not
  // money. Kept visually distinct from pendingSub (which is a ₹ commission
  // projection the backend no longer computes at this level) so a reader
  // never mistakes "3 pending" here for a rupee figure.
  const pendingUnitsSub = (value?: number): React.ReactNode => (
    !value ? undefined : (
      <>
        <span className="block text-amber-600">+{value}</span>
        <span className="block font-normal">pending</span>
      </>
    )
  );

  // Sale revenue (not incentive payout) for the same paid-but-pending orders
  // pendingUnitsSub counts. Labelled "pending sale" rather than reusing
  // pendingSub's bare "pending" — this is order value, typically many times
  // the eventual commission, and showing it under an "Incentives" figure
  // with the same wording as a commission projection would read as one.
  const pendingSaleSub = (value?: number): React.ReactNode => (
    !value ? undefined : (
      <>
        <span className="block text-amber-600">{inr(value)}</span>
        <span className="block font-normal">pending sale</span>
      </>
    )
  );

  const pctOfUploaded = (value: number | null | undefined): React.ReactNode => (
    value === null || value === undefined ? undefined : (
      <>
        <span className="block">{value}%</span>
        <span className="block font-normal">of uploaded</span>
      </>
    )
  );

  const personRow = (r: Row, indent = false, sno?: number) => (
    <tr key={r.mobile} className="hover:bg-slate-50/70 transition-colors">
      <td className="px-4 py-3.5 text-center text-slate-400 font-semibold text-[12px]">{sno}</td>
      <td className="px-4 py-3.5">
        <div className={`flex justify-start ${indent ? 'pl-9' : ''}`}>
          <div className="inline-flex flex-col items-start">
            <div className="font-bold text-slate-800 text-[13px] whitespace-nowrap">{r.name || '—'}</div>
            <MaskedMobile mobile={r.mobile} className="text-slate-500 font-medium text-[11px]" />
            <div className="text-[10px] text-slate-400">{r.role}</div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3.5 text-center text-slate-700 font-bold text-[13px]">{r.target_units || '—'}</td>
      <td className="px-4 py-3.5 text-center text-slate-800 font-black text-[13px]">
        {r.units_total}
        {(r.units_direct_pending || r.units_indirect_pending) ? (
          <span className="block text-[10px] font-semibold text-slate-400">
            +{(r.units_direct_pending || 0) + (r.units_indirect_pending || 0)} pending
          </span>
        ) : null}
      </td>
      <td className="px-4 py-3.5 text-center text-indigo-700 font-black text-[13px]">
        {r.units_direct}
        {r.units_direct_pending ? (
          <span className="block text-[10px] font-semibold text-slate-400">+{r.units_direct_pending} pending</span>
        ) : null}
      </td>
      <td className="px-4 py-3.5 text-center text-purple-700 font-black text-[13px]">
        {r.units_indirect}
        {r.units_indirect_pending ? (
          <span className="block text-[10px] font-semibold text-slate-400">+{r.units_indirect_pending} pending</span>
        ) : null}
      </td>
      <td className="px-4 py-3.5 text-center text-amber-700 font-black text-[13px] whitespace-nowrap">
        {inr(r.total_earned_amount)}
        {(r.direct_pending_sales_amount || r.indirect_pending_sales_amount) ? (
          <span className="block text-[10px] font-semibold text-slate-400">
            +{inr((r.direct_pending_sales_amount || 0) + (r.indirect_pending_sales_amount || 0))} pending sale
          </span>
        ) : null}
      </td>
      <td className="px-4 py-3.5 text-center text-indigo-700 font-bold text-[12px] whitespace-nowrap">
        {inr(r.directly_earned_amount)}
        {r.direct_pending_sales_amount ? (
          <span className="block text-[10px] font-semibold text-slate-400">+{inr(r.direct_pending_sales_amount)} pending sale</span>
        ) : null}
      </td>
      <td className="px-4 py-3.5 text-center text-purple-700 font-bold text-[12px] whitespace-nowrap">
        {inr(r.indirect_earned_amount)}
        {r.indirect_pending_sales_amount ? (
          <span className="block text-[10px] font-semibold text-slate-400">+{inr(r.indirect_pending_sales_amount)} pending sale</span>
        ) : null}
      </td>
      <td className="px-4 py-3.5 text-center">
        {/* Null means no target was set — distinct from 0%, which would read as
            a failure rather than an absence. */}
        <span className={`text-[12px] font-black ${
          r.achievement_pct === null ? 'text-slate-400'
            : r.achievement_pct >= 100 ? 'text-emerald-700' : 'text-slate-600'}`}>
          {r.achievement_pct === null ? 'no target' : `${r.achievement_pct}%`}
        </span>
      </td>
      <td className="px-4 py-3.5 text-center text-slate-700 font-bold text-[13px]">{r.leads_uploaded}</td>
      <td className="px-4 py-3.5 text-center text-slate-700 font-bold text-[13px]">{r.leads_uploaded_used}</td>
    </tr>
  );

  const headers: { label: string; hint?: string }[] = [
    { label: 'S.No' },
    { label: 'Person' },
    { label: 'Target' },
    { label: 'Total Units' },
    { label: 'Direct Units' },
    { label: 'Indirect Units' },
    { label: 'Total Incentives' },
    { label: 'Direct Incentives' },
    { label: 'Indirect Incentives' },
    { label: 'Achieved' },
    { label: 'Leads Assigned', hint: 'Leads this person brought in themselves' },
    { label: 'Status Updated', hint: 'Of the leads this person brought in, how many have had their status updated (moved off "New")' },
  ];

  return (
    <div className="w-full space-y-6 pb-12 font-sans">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Options come from the last unfiltered fetch (allPeople), not the
            currently-filtered `rows` — so picking one person doesn't collapse
            the list down to just them, while still never offering somebody
            outside the caller's scope. */}
        <Select
          value={person}
          onChange={(v) => setPerson(v)}
          options={[
            { value: '', label: 'All persons' },
            ...allPeople.map((r) => personOption(r, { detail: r.role, status: true })),
          ]}
          renderValue={staffFilterValue(allPeople)}
          className={`${inputCls} w-[13rem] truncate`}
        />

        <div className="flex flex-wrap items-center gap-3">
          <Select
            value={month}
            onChange={(v) => setMonth(v)}
            options={months}
            className={`${inputCls} w-[11rem] truncate`}
          />
          {person && (
            <button type="button"
              onClick={() => setPerson('')}
              className="flex items-center justify-center h-[42px] px-4 text-sm border border-slate-200 shadow-sm rounded-xl bg-white text-slate-600 outline-none hover:bg-slate-50 hover:ring-2 hover:ring-emerald-500/20 transition-all duration-200 cursor-pointer font-semibold">
              Clear
            </button>
          )}
          <button type="button"
            onClick={load}
            disabled={loading}
            title="Refresh"
            className={`flex items-center justify-center gap-2 h-[42px] px-4 text-sm border border-slate-200 shadow-sm rounded-xl bg-white text-slate-600 outline-none hover:bg-slate-50 hover:ring-2 hover:ring-emerald-500/20 transition-all duration-200 cursor-pointer font-semibold ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}>
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 md:p-6 space-y-5 animate-pulse">
          <div className="flex flex-wrap gap-x-10 gap-y-4">
            {[1, 2, 3, 4, 5, 6, 7].map((i) => (
              <div key={i} className="space-y-1.5">
                <div className="h-2.5 w-16 bg-slate-200 rounded-md" />
                <div className="h-5 w-14 bg-slate-200 rounded-md" />
              </div>
            ))}
          </div>
          <div className="flex items-center justify-end pt-4 border-t border-slate-100">
            <div className="h-[30px] w-20 bg-slate-100 rounded-lg" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-[200px] bg-slate-100 rounded-xl" />
            ))}
          </div>
        </div>
      ) : totals && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 md:p-6 space-y-5">
          <div className="flex flex-wrap gap-x-10 gap-y-4">
            {tile('Target units', totals.target_units)}
            {tile('Total units', totals.units_total, 'text-emerald-700',
              pendingUnitsSub((totals.units_direct_pending || 0) + (totals.units_indirect_pending || 0)))}
            {tile('Direct units', totals.units_direct, 'text-indigo-700', pendingUnitsSub(totals.units_direct_pending))}
            {tile('Indirect units', totals.units_indirect, 'text-purple-700', pendingUnitsSub(totals.units_indirect_pending))}
            {tile('Total Incentives', inr(totals.total_earned_amount), 'text-amber-700',
              pendingSaleSub((totals.direct_pending_sales_amount || 0) + (totals.indirect_pending_sales_amount || 0)))}
            {tile('Direct incentives', inr(totals.directly_earned_amount), 'text-indigo-700', pendingSaleSub(totals.direct_pending_sales_amount))}
            {tile('Indirect incentives', inr(totals.indirect_earned_amount), 'text-purple-700', pendingSaleSub(totals.indirect_pending_sales_amount))}
            {/* The farm side of the same window — what the executives who
                conducted the visits earned on these sales. */}
            {tile('Farm incentives', inr(totals.farm_incentives || 0), 'text-emerald-700', pendingSub(totals.pending_farm_incentives))}
            {tile('Farm sales', totals.farm_sales || 0, 'text-emerald-700',
              totals.farm_executives ? `${totals.farm_executives} executive${totals.farm_executives === 1 ? '' : 's'}` : undefined)}
            {tile('Farm visits', totals.farm_visits || 0, 'text-emerald-700')}
          </div>

          {/* Farm executives get their own table rather than lines in the
              marketing one above: they have no direct/indirect split, no
              leads and no marketing target, so they would read as an
              all-zero row and imply the totals cover them. */}
          {farmRows.length > 0 && (
            <div className="pt-4 border-t border-slate-100">
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                Farm Executives
              </p>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100">
                      {['Executive', 'Visits', 'Sales', 'Earned', 'Pending'].map((h, i) => (
                        <th
                          key={h}
                          className={`py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap ${i ? 'text-right' : 'text-left'}`}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {farmRows.map((f) => (
                      <tr key={f.mobile} className="border-b border-slate-50 last:border-0">
                        <td className="py-2">
                          <span className="text-sm font-semibold text-slate-800">{f.name || f.mobile}</span>
                          <span className="block text-[10px] text-slate-400">{f.role}</span>
                        </td>
                        <td className="py-2 text-right text-sm text-slate-700">{f.visits}</td>
                        <td className="py-2 text-right text-sm text-slate-700">{f.sales}</td>
                        <td className="py-2 text-right text-sm font-bold text-emerald-700">
                          {inr(f.earned)}
                        </td>
                        <td className="py-2 text-right text-xs font-semibold text-amber-600">
                          {f.pending_incentive_amount ? inr(f.pending_incentive_amount) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex items-center justify-end pt-4 border-t border-slate-100">
            <ChartTypeToggle value={splitChartType} onChange={setSplitChartType} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <p className="text-xs font-bold text-slate-500 text-center mb-1">Target vs Total Units</p>
              <div className="h-[200px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  {splitChartType === 'pie' ? (
                    <PieChart>
                      <Tooltip content={<GenericSplitTooltip />} />
                      <Pie data={targetTotalPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={75} paddingAngle={3} animationDuration={1000} animationEasing="ease-out" label={({ payload }) => payload.displayValue} labelLine={false}>
                        <Cell fill="#94a3b8" stroke="#fff" strokeWidth={2} />
                        <Cell fill="#10b981" stroke="#fff" strokeWidth={2} />
                      </Pie>
                    </PieChart>
                  ) : (
                    <BarChart data={targetTotalBarLineData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10 }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10 }} width={30} allowDecimals={false} />
                      <Tooltip content={<MiniMultiSeriesTooltip />} cursor={{ fill: '#f8fafc' }} />
                      <Bar dataKey="Target" fill="#94a3b8" radius={[4, 4, 0, 0]} maxBarSize={36} animationDuration={1000} animationEasing="ease-out" />
                      <Bar dataKey="Total" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={36} animationDuration={1000} animationEasing="ease-out" />
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </div>
              <div className="flex items-center justify-center gap-4 text-[11px] font-semibold text-slate-500">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#94a3b8' }} />Target</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#10b981' }} />Total</span>
              </div>
            </div>
            <div>
              <p className="text-xs font-bold text-slate-500 text-center mb-1">Units: Direct vs Indirect</p>
              <div className="h-[200px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  {splitChartType === 'pie' ? (
                    <PieChart>
                      <Tooltip content={<GenericSplitTooltip />} />
                      <Pie data={unitsSplitData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={75} paddingAngle={3} animationDuration={1000} animationEasing="ease-out" label={({ payload }) => payload.displayValue} labelLine={false}>
                        <Cell fill="#465FFF" stroke="#fff" strokeWidth={2} />
                        <Cell fill="#7C93F2" stroke="#fff" strokeWidth={2} />
                      </Pie>
                    </PieChart>
                  ) : (
                    <BarChart data={unitsSplitBarData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10 }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10 }} width={30} allowDecimals={false} />
                      <Tooltip content={<MiniMultiSeriesTooltip />} cursor={{ fill: '#f8fafc' }} />
                      <Bar dataKey="Direct" fill="#465FFF" radius={[4, 4, 0, 0]} maxBarSize={36} animationDuration={1000} animationEasing="ease-out" />
                      <Bar dataKey="Indirect" fill="#7C93F2" radius={[4, 4, 0, 0]} maxBarSize={36} animationDuration={1000} animationEasing="ease-out" />
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </div>
              <div className="flex items-center justify-center gap-4 text-[11px] font-semibold text-slate-500">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#465FFF' }} />Direct</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#7C93F2' }} />Indirect</span>
              </div>
            </div>
            <div>
              <p className="text-xs font-bold text-slate-500 text-center mb-1">Incentives: Direct vs Indirect</p>
              <div className="h-[200px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  {splitChartType === 'pie' ? (
                    <PieChart>
                      <Tooltip content={<GenericSplitTooltip />} />
                      <Pie data={incentivesSplitData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={75} paddingAngle={3} animationDuration={1000} animationEasing="ease-out" label={({ payload }) => payload.displayValue} labelLine={false}>
                        <Cell fill="#465FFF" stroke="#fff" strokeWidth={2} />
                        <Cell fill="#7C93F2" stroke="#fff" strokeWidth={2} />
                      </Pie>
                    </PieChart>
                  ) : (
                    <BarChart data={incentivesSplitBarData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10 }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10 }} width={45} tickFormatter={(v) => inr(v)} />
                      <Tooltip content={<MiniMultiSeriesTooltip />} cursor={{ fill: '#f8fafc' }} />
                      <Bar dataKey="Direct" fill="#465FFF" radius={[4, 4, 0, 0]} maxBarSize={36} animationDuration={1000} animationEasing="ease-out" />
                      <Bar dataKey="Indirect" fill="#7C93F2" radius={[4, 4, 0, 0]} maxBarSize={36} animationDuration={1000} animationEasing="ease-out" />
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </div>
              <div className="flex items-center justify-center gap-4 text-[11px] font-semibold text-slate-500">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#465FFF' }} />Direct</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#7C93F2' }} />Indirect</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Where this period's leads came from and how far each source got.
          Scoped to the caller's own people, unlike the org block below it. */}
      {!error && (
        <LeadConversionCard data={conversion} period={period} loading={loading} />
      )}

      {org && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 md:p-6 space-y-5">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <h2 className="text-sm font-black text-slate-700 uppercase tracking-wider">
              Lead status overview
            </h2>
            {/* Named explicitly because it is the ONE block on this page that is
                not scoped to the caller: it is the denominator everyone's own
                numbers sit against. Counts only, no names. */}
            <span className="text-[11px] text-slate-400">
              every lead the company took in during {period}, whoever owns it
            </span>
          </div>

          <div className="flex flex-wrap gap-x-10 gap-y-4">
            {tile('Uploaded', org.uploaded)}
            {tile('Status Updated', org.used, 'text-[#465FFF]', pctOfUploaded(org.usage_pct))}
            {tile('Not Updated', org.unused, 'text-[#c85526]')}
            {tile('Onboarded', org.onboarded, 'text-indigo-700', pctOfUploaded(org.onboarded_pct))}
          </div>

          {trend.length > 0 && (
            <div className="pt-4 border-t border-slate-100">
              <div className="flex items-center justify-between mb-4">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Lead status overview, month wise
                </p>
                <ChartTypeToggle value={trendChartType} onChange={setTrendChartType} />
              </div>

              {trendChartType === 'pie' ? (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs font-bold text-slate-500 text-center mb-1">Uploaded</p>
                    <div className="h-[260px] w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Tooltip content={<CustomLeadTooltip />} />
                          <Pie data={trendChartData} dataKey="Uploaded" nameKey="name" cx="50%" cy="50%" innerRadius={35} outerRadius={70} paddingAngle={2} animationDuration={1000} animationEasing="ease-out" label={({ value }) => (value > 0 ? value : '')} labelLine={false}>
                            {trendChartData.map((_, index) => (
                              <Cell key={`uploaded-cell-${index}`} fill={LEAD_PIE_COLORS[index % LEAD_PIE_COLORS.length]} stroke="#fff" strokeWidth={2} />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-[#465FFF] text-center mb-1">Status updated</p>
                    <div className="h-[260px] w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Tooltip content={<CustomLeadTooltip />} />
                          <Pie data={trendChartData} dataKey="Used" nameKey="name" cx="50%" cy="50%" innerRadius={35} outerRadius={70} paddingAngle={2} animationDuration={1000} animationEasing="ease-out" label={({ value }) => (value > 0 ? value : '')} labelLine={false}>
                            {trendChartData.map((_, index) => (
                              <Cell key={`used-cell-${index}`} fill={LEAD_PIE_COLORS[index % LEAD_PIE_COLORS.length]} stroke="#fff" strokeWidth={2} />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-[#eb6834] text-center mb-1">Not updated</p>
                    <div className="h-[260px] w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Tooltip content={<CustomLeadTooltip />} />
                          <Pie data={trendChartData} dataKey="Unused" nameKey="name" cx="50%" cy="50%" innerRadius={35} outerRadius={70} paddingAngle={2} animationDuration={1000} animationEasing="ease-out" label={({ value }) => (value > 0 ? value : '')} labelLine={false}>
                            {trendChartData.map((_, index) => (
                              <Cell key={`unused-cell-${index}`} fill={LEAD_PIE_COLORS[index % LEAD_PIE_COLORS.length]} stroke="#fff" strokeWidth={2} />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  {/* Shared color key — all three pies above cycle the same
                      palette in the same month order, so one legend covers
                      all of them instead of repeating it three times. */}
                  <div className="sm:col-span-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 pt-1">
                    {trendChartData.map((entry, index) => (
                      <span key={entry.name} className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500">
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: LEAD_PIE_COLORS[index % LEAD_PIE_COLORS.length] }} />
                        {entry.name}
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="h-[320px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={trendChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} dy={10} interval={0} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} width={40} allowDecimals={false} />
                      <Tooltip content={<CustomLeadTooltip />} cursor={{ fill: '#f8fafc' }} />
                      <Bar dataKey="Uploaded" name="Uploaded" fill="#94a3b8" radius={[4, 4, 0, 0]} maxBarSize={32} animationDuration={1000} animationEasing="ease-out" />
                      <Bar dataKey="Used" name="Status updated" fill="#465FFF" radius={[4, 4, 0, 0]} maxBarSize={32} animationDuration={1000} animationEasing="ease-out" />
                      <Bar dataKey="Unused" name="Not updated" fill="#eb6834" radius={[4, 4, 0, 0]} maxBarSize={32} animationDuration={1000} animationEasing="ease-out" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {loading && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b-2 border-slate-100 bg-slate-50/60">
                {headers.map((h) => (
                  <th key={h.label} title={h.hint}
                    className={`px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap ${h.label === 'Person' ? 'text-left' : 'text-center'}`}>{h.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <TableSkeleton rows={6} cols={headers.length || 12} />
            </tbody>
          </table>
        </div>
      )}
      {!loading && error && <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-12 text-center text-rose-500 font-medium">{error}</div>}
      {!loading && !error && rows.length === 0 && <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-12 text-center text-slate-400 font-medium">No marketing activity for this period.</div>}

      {!loading && !error && rows.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b-2 border-slate-100">
                {headers.map((h) => (
                  <th key={h.label} title={h.hint}
                    className={`px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap ${h.label === 'Person' ? 'text-left' : 'text-center'}`}>{h.label}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {view === 'person' || !canGroupByLead
                ? rows.map((r, i) => personRow(r, false, i + 1))
                : (() => {
                    let sno = 0;
                    return groups.map((g) => (
                      <React.Fragment key={g.mobile}>
                        <tr className="bg-slate-50/80">
                          <td className="px-4 py-2.5 text-center text-slate-400 font-semibold text-[12px]">{++sno}</td>
                          <td className="px-4 py-2.5">
                            <div className="flex justify-start">
                              <div className="inline-flex flex-col items-start">
                                <div className="font-black text-slate-800 text-[13px]">{g.name || g.mobile}</div>
                                <div className="text-[10px] text-slate-500">
                                  {[g.role, `${g.member_count} ${g.member_count === 1 ? 'person' : 'people'}`]
                                    .filter(Boolean).join(' · ')}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-2.5 text-center font-bold text-slate-600 text-[12px]">{g.target_units || '—'}</td>
                          <td className="px-4 py-2.5 text-center font-black text-slate-800 text-[12px]">{g.units_total}</td>
                          <td className="px-4 py-2.5 text-center font-black text-indigo-700 text-[12px]">{g.units_direct}</td>
                          <td className="px-4 py-2.5 text-center font-black text-purple-700 text-[12px]">{g.units_indirect}</td>
                          <td className="px-4 py-2.5 text-center font-black text-amber-700 text-[12px] whitespace-nowrap">{inr(g.total_earned_amount)}</td>
                          <td className="px-4 py-2.5" colSpan={2} />
                          <td className="px-4 py-2.5" />
                          <td className="px-4 py-2.5 text-center font-bold text-slate-600 text-[12px]">{g.leads_uploaded}</td>
                          <td className="px-4 py-2.5 text-center font-bold text-slate-600 text-[12px]">{g.leads_uploaded_used}</td>
                        </tr>
                        {g.members.map((m) => personRow(m, true, ++sno))}
                      </React.Fragment>
                    ));
                  })()}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MarketingSummaryTab;
